import { Box, Button, Link, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarDate, F0Icon, F0TagRaw } from '@factorialco/f0-react'
import { Calendar, ChevronRight } from '@factorialco/f0-react/icons/app'
import {
  CreateTrainingsTrainingClassTrainingSessionsInput,
  TrainingsSessionScheduleEnum,
} from 'generated/resources/graphql'
import moment from 'moment'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import { formatSessionDate } from 'modules/trainings/lib/utils'

interface EventPreviewProps {
  event: CreateTrainingsTrainingClassTrainingSessionsInput
  index: number
  onDelete: (value: number) => void
  onDuplicate: (value: number) => void
  onEdit: (value: number) => void
}

export const EventPreview = ({
  event,
  index,
  onDelete,
  onDuplicate,
  onEdit,
}: EventPreviewProps) => {
  const sessionDate = formatSessionDate({
    startsAt: event?.startsAt ?? '',
    endsAt: event?.endsAt ?? '',
    duration: event.duration,
    schedule: event.schedule,
  })

  const translateSchedule = (schedule?: TrainingsSessionScheduleEnum | null) => {
    if (schedule === 'scheduled') {
      return i18n.t(
        'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_preview.scheduled'
      )
    }

    return i18n.t(
      'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_preview.self_paced'
    )
  }

  // we shouldn't do this, but the design is kinda using the CalendarEvent from factorial one, but reinvented,
  // therefore we don't have the colors available here which are Barbie/Solid/50 for scheduled and Info/Solid/50 for self paced
  // TODO: remove this asap in order to avoid being unsynced with the f1 design tokens
  const scheduleTypeColor = event.schedule === 'scheduled' ? '#F053A0' : '#5596F6'
  return (
    <Box
      borderRadius={{ all: 'abs016' }}
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      padding="s16"
      flexDirection="row"
      alignItems={event.link || event.location ? 'flexStart' : 'center'}
      justifyContent="spaceBetween"
    >
      <Box flexDirection="row" gap="s16">
        <Box
          minHeight="full"
          style={{ backgroundColor: scheduleTypeColor }}
          borderRadius={{ all: 'abs016' }}
          width="s4"
        ></Box>
        <Box gap="s8">
          <Text size="100" color="grey600">
            {translateSchedule(event.schedule)}
          </Text>
          <Text size="300" weight="semibold" color="grey1000">
            {event.name}
          </Text>
          <Box gap="s8" flexDirection="row" alignItems="center">
            <Text size="300" weight="normal" color="textSecondary">
              {sessionDate}
            </Text>
            {event.location && (
              <Text size="200" weight="normal">
                {event.location}
              </Text>
            )}
            {event.link && (
              <Link.Href external href={event.link}>
                {i18n.t('trainings.trainings_revamp.training_detail.link_placeholder')}
              </Link.Href>
            )}
          </Box>
          {event.recurrent && (
            <Box maxWidth="fit-content">
              <F0TagRaw icon={Calendar} text={'you'} />
            </Box>
          )}
        </Box>
      </Box>
      <Box height="full">
        <Box flexDirection="row" gap="s8">
          <CalendarTag
            startDate={event.startsAt}
            endDate={event.dueDate}
            isRecurrent={event.recurrent}
          />
          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <Button iconName="ellipsis" type="secondary" />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="start">
              <PopoverMenu.TouchableOption
                title={i18n.t(
                  'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_preview.edit'
                )}
                onClick={() => onEdit(index)}
              />
              <PopoverMenu.TouchableOption
                title={i18n.t(
                  'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_preview.duplicate'
                )}
                onClick={() => onDuplicate(index)}
              />
              <PopoverMenu.TouchableOption
                title={i18n.t(
                  'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_preview.delete'
                )}
                onClick={() => onDelete(index)}
              />
            </PopoverMenu.Content>
          </PopoverMenu>
        </Box>
      </Box>
    </Box>
  )
}

const CalendarTag = ({
  startDate,
  endDate,
  isRecurrent,
}: {
  startDate?: string | null
  endDate?: string | null
  isRecurrent: boolean
}) => {
  if (!startDate && !endDate) return <></>

  if (startDate && !endDate) {
    return <F0AvatarDate date={moment(startDate).toDate()} />
  }

  if (!startDate && endDate) {
    return <F0AvatarDate date={moment(endDate).toDate()} />
  }

  return (
    <Box flexDirection="row" gap="s8" alignItems="center">
      <F0AvatarDate date={moment(startDate).toDate()} />
      <Text.Normal color="grey600">
        <F0Icon icon={ChevronRight} size="sm" />
      </Text.Normal>
      <F0AvatarDate date={moment(endDate).toDate()} />
    </Box>
  )
}
